import { Component, OnInit, Input, ViewEncapsulation, ViewChild } from '@angular/core';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { ModuleManager } from 'src/shared/models/module-manager';
import ServiceLevelService, { ServiceLevelData } from "./service-level.service";
import { ChartConfiguration } from 'chart.js';
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { resolve } from 'chart.js/helpers';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { BaseChartDirective } from 'ng2-charts';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'service-level',
    templateUrl: './service-level.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./service-level.component.scss']
})
export class ServiceLevelComponent implements OnInit {

    @Input() public moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;

    public chartConfig = null;
    public data: ServiceLevelData = null; // A copy of data returned by loadData will be stored here.
    public filterValue = ""; // This variable contains the contents of the search bar.
    public showFilter = false;

    public slTotal = '';

    public somethingWentWrong = false;

    //Variables for Chart
    public state = null;
    public dataLoadSuccess = false;
    public totalLabelSize: number;
    // Chart.js Setup
    chartType = 'doughnut';
    chartPadding = {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
    };
    elementContext = null;

    _chartConstants = {
        colors: [ // This list is in the order of Chart dataset backgroundColor values
            "rgba(189, 32, 38, 1)", // OutService or Negative
            "rgba(61, 150, 73, 1)", // InService or Positive
        ],
        colorsLight: undefined,
        borderColor: undefined,
        borderWidth: 5,
        hoverBorderWidth: 0,
        fontColor: "#353a40", // theUpsideDown color
        fontFamily: "'Lato-Regular', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'", // Includes fallbacks (from default ChartJS config)
        fontSize: 13,
    };

    _cxoneChartConstants = {
        colors: [ // This list is in the order of Chart dataset backgroundColor values
            "rgba(189, 32, 38, 1)", // OutService or Negative
            "rgba(61, 150, 73, 1)", // InService or Positive
        ],
        colorsLight: [ // This list is in the order of Chart dataset backgroundColor values
            "rgba(189, 32, 38, .5)", // OutService or Negative
            "rgba(61, 150, 73, .5)", // InService or Positive
        ],
        borderColor: 'rgba(121, 138, 146, 1)', // dark grey
        borderWidth: 1,
        hoverBorderWidth: 1,
        fontColor: "#353a40", // theUpsideDown color
        fontFamily: "'OpenSans-SemiBold', 'Lato-Regular', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'", // Includes fallbacks (from default ChartJS config)
        fontSize: 13,
    };

    isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    private isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.

    constructor(
        private service: ServiceLevelService,
        private cvLocalizationService: CVLocalizationService,
        private config: CvConfigurationService,
        private datePipe: DatePipe,
    ) { }

    public get IsLoadingData() { return this.isDataLoading; }

    async ngOnInit() {
        this.moduleManager.container.toolbarHidden = this.config.isCxOne;
        this.moduleManager.showLoader = true;
        await this.cvLocalizationService.GetSection("Service_Level");

        // Module Configuration:
        this.moduleManager.setOptions({
            enableWidthClass: false, // enabling this will add mediaquery-like classes to 'moduleResponsiveContainer'
            widgetMode: false, // enabling this disables the module header and moves the menu to the body of the module
            onSettingsApplied: settings => {
                this.loadData(false);
            },
            hasRefresh: true, // hasRefresh determines if a module has a refresh menu item, this item will call onRefresh when clicked
            // in general refreshType should not be changed, it will be determined automatically based upon metrics you have.
            // if it does need to be changed, see Module Container's moduleOptions object for more information
            refreshType: ModuleManagerRefreshType.Default,
            onRefresh: isAutoRefresh => {
                if (!isAutoRefresh) { this.moduleManager.showLoader = true; };
                this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading, // determines whether a module's refresh menu item is clickable.  Can't refresh when there's already one in progress.
        });
        this.moduleManager.container.toolbarHidden = this.config.isCxOne;

        this.moduleManager.showLoader = true;

        // Module Configuration:

        this.moduleManager.registerEvents({
            onResize: () => this.setTotalLabelSize()
        });

        this.moduleManager.addState({
            name: 'chartState',
            autoLoad: true,
            getStateData: () => this.state,
            onStateLoaded: (stateData) => {
                if (!stateData)
                    return;
                this.state = stateData;
            }
        });

        this.moduleManager.addMenuItem({
            name: "Legend_Position",
            icon: 'legend',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null,
            subItems: [{
                name: "Top", // Menu items are localized automatically by Module Container.  Keys under section Module_Menu
                icon: 'positionTop',
                click: () => this.setLegendPosition('top'),
            },
            {
                name: "Bottom",
                icon: 'positionBottom',
                click: () => this.setLegendPosition('bottom')
            },
            {
                name: "Left",
                icon: 'positionLeft',
                click: () => this.setLegendPosition('left')
            },
            {
                name: "Right",
                icon: 'positionRight',
                click: () => this.setLegendPosition('right')
            }]
        } as ModuleManagerSubMenu);

        this._chartConstants = this.config.isCxOne ? this._cxoneChartConstants : this._chartConstants;
    }

    setLegendPosition(position) {
        this.state.legendPosition = position;
        this.chartConfig.options.plugins.legend.position = position;
        this.loadData(false);
        this.moduleManager.saveState('chartState');
        this.moduleManager.requestPublishNeeded();
    }

    setTotalLabelSize() {
        this.totalLabelSize = this.chartObject?.chart.width / 9;
    }

    drawCenterLabel(chart) {
        if (chart.config.options.elements.center) {
            //Get ctx from string
            const ctx = chart.ctx;

            //Get options from the center object in options
            const centerConfig = chart.config.options.elements.center;
            const fontStyle = centerConfig.fontStyle || this._chartConstants.fontFamily;

            //Make service level a percentage
            const slPercent = parseFloat((this.data?.ServiceLevel * 100).toFixed(2));
            let txt =  slPercent.toString() + "%";


            if (txt.length === 2) { // in the case it is 0 percent making the text smaller
                txt = " " + txt + " ";
            }
            this.slTotal = txt; //for popover text
            const sidePadding = centerConfig.sidePadding || 20;
            const innerRadius = chart._metasets[0].data[0].innerRadius;

            const sidePaddingCalculated = (sidePadding / 100) * (innerRadius * 2);

            //Start with a base font of 30px
            ctx.font = "30px " + fontStyle;

            //Get the width of the string and also the width of the element minus 10 to give it 5px side padding
            const stringWidth = ctx.measureText(txt).width;
            const elementWidth = (innerRadius * 2) - sidePaddingCalculated;

            // Find out how much the font can grow in width.
            const widthRatio = elementWidth / stringWidth;
            const newFontSize = this.config.isCxOne ? Math.floor(20 * widthRatio) : Math.floor(27 * widthRatio);
            const elementHeight = (innerRadius * 2);

            // Pick a new font size so it will not be larger than the height of label.
            const fontSizeToUse = Math.min(newFontSize, elementHeight);

            //Set font settings to draw it correctly.
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const centerX = ((chart.chartArea.left + chart.chartArea.right) / 2);
            const centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2);
            ctx.font = fontSizeToUse + "px " + fontStyle;
            ctx.fillStyle = this._chartConstants.fontColor;

            //Draw text in center
            ctx.fillText(txt, centerX, centerY);
        }
    }

    createChart() {
        // Check for null/empty datasets
        // If found, create an empty chart
        if (this.data === null) {
            return;
        }

        if (this.state == null || this.state.legendPosition == null) {
            this.state = {
                legendPosition: 'top',
                dataPadding: '5% 0 0 0'
            };
        }

        //
        // Make Chart
        //
        const preparedLabels = [
            this.getLocalization("Out_SLA","Out SLA"),
            this.getLocalization("In_SLA", "In SLA"),
        ];

        this.chartConfig = {
            type: 'doughnut',
            data: {
                datasets: [{
                    data: [this.data.OutService, this.data.InService],
                    backgroundColor: this._chartConstants.colors,
                    borderWidth: this._chartConstants.borderWidth,
                    borderColor: this.config.isCxOne ? 'rgba(121, 138, 146, 1)' : undefined,
                    hoverBorderColor: this.config.isCxOne ? 'rgba(121, 138, 146, 1)' : undefined,
                    // datalabels: These labels are drawn below in the custom plugin afterDatasetsDraw
                    hoverBackgroundColor: this._chartConstants.colorsLight,
                    hoverBorderWidth: this._chartConstants.hoverBorderWidth,
                }],
                labels: preparedLabels
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                elements: {
                    center: {
                        text: "", // The actual content of the center is drawn by the beforeDraw custom plugin below
                        color: this._chartConstants.fontColor,
                        fontFamily: this._chartConstants.fontFamily,
                    },
                },
                plugins: {
                    emptyChart: {
                        disableCxone: this.config.isCxOne,
                    },
                    tooltip: {
                        enabled: true,
                        padding: this.config.isCxOne ? 12 : 6,
                        usePointStyle: true,
                        boxPadding: this.config.isCxOne ? 3 : 1,
                        boxWidth: this.config.isCxOne ? 11 : 18,
                        boxHeight: this.config.isCxOne ? 11 : 18,
                        callbacks: {
                            title: tooltipItemArray =>
                                this.config.isCxOne ? this.datePipe.transform(new Date(), "MMM dd, yyyy") : "",
                            label: tooltipItem => tooltipItem.label,
                            footer: tooltipItem => {
                                const dataset = tooltipItem[0].dataset as any;
                                const total = dataset.data.reduce((a, b) => a + b);
                                const percent = (((dataset.data[tooltipItem[0].dataIndex] as any) / (total as any)) * 100).toFixed(2);
                                const cxoneFooter = '    Contact: ' + percent + '%';
                                //for PM footer
                                const avgTxt = this.getLocalization("Average", "Average");
                                const countTxt = this.getLocalization("Count", "Count");
                                const pmFooter = `${avgTxt}: ${percent}% \n${countTxt}: ${dataset.data[tooltipItem[0].dataIndex]}`;
                                return this.config.isCxOne ? cxoneFooter : pmFooter;
                            },
                            labelColor: context => ({
                                borderColor: 'rgb(255, 255, 255)',
                                backgroundColor: context.dataset.backgroundColor[context.dataIndex],
                                borderWidth: this.config.isCxOne ? 0 : 0.5,
                            }),
                            labelPointStyle: context => ({
                                pointStyle: this.config.isCxOne ? 'circle' : 'rect',
                                rotation: 0,
                            }),
                        },
                        backgroundColor: this.config.isCxOne ? '#FFF' : 'rgba(0, 0, 0, 0.8)',
                        titleFont: {
                            size: this.config.isCxOne ? 12 : 16,
                            weight: this.config.isCxOne ? "normal" : "bold",
                        },
                        titleColor: this.config.isCxOne ? '#8c95a5' : '#fff',
                        bodyColor: this.config.isCxOne ? '#353a40' : '#fff',
                        footerColor: this.config.isCxOne ? '#353a40' : '#fff',
                        bodyFont: {
                            size: 14,
                            weight: this.config.isCxOne ? "normal" : "bold",
                        },
                        footerFont: {
                            size: 14,
                            weight: this.config.isCxOne ? "bold" : "normal",
                        },
                        borderColor: this.config.isCxOne ? '#D0D2D3' : '#353a40',
                        borderWidth: this.config.isCxOne ? 1 : 0,
                        cornerRadius: this.config.isCxOne ? 0 : 6,
                    },
                    legend: {
                        display: true,
                        position: this.state.legendPosition,
                        labels: {
                            boxWidth: 13,
                            color: this._chartConstants.fontColor,
                            font: {
                                family: this._chartConstants.fontFamily,
                                size: this._chartConstants.fontSize,
                            },
                            usePointStyle: !this.config.isCxOne ? true : undefined,
                            //If we are on PM we don't want the custom generated labels, hence we are setting undefined here
                            generateLabels: !this.config.isCxOne ? undefined : (chart) => { // adds custom labels to the chart legend
                                const data = chart.data;
                                if (data.labels.length && data.datasets.length) {
                                    return data.labels.map((label, i) => {
                                        const meta = chart.getDatasetMeta(0);
                                        const ds = data.datasets[0];
                                        const arc = meta.data[i];
                                        const custom = arc && arc.options || {};
                                        const arcOpts = chart.options.elements.arc;
                                        const fill = custom.backgroundColor ? custom.backgroundColor :
                                            resolve([ds.backgroundColor, arcOpts.backgroundColor], chart.ctx , i);
                                        const stroke = custom.borderColor ? custom.borderColor :
                                            resolve([ds.borderColor, arcOpts.borderColor], chart.ctx, i);
                                        const bw = custom.borderWidth ? custom.borderWidth :
                                            resolve([ds.borderWidth, arcOpts.borderWidth], chart.ctx, i);

                                        // We get the value of the current label
                                        const value = chart.config.data.datasets[0].data[i];
                                        const toggle = chart.getDataVisibility(i);

                                        return {
                                            // Instead of `text: label,`
                                            // We add the value to the string
                                            text: label + " (" + value + ")",
                                            fillStyle: fill,
                                            strokeStyle: stroke,
                                            lineWidth: bw,
                                            hidden: !toggle,
                                            index: i
                                        };
                                    });
                                }
                            },
                        },
                    },
                },
                layout: {
                    padding: {
                        left: 8,
                        right: 8,
                        top: 8,
                        bottom: 8
                    }
                },
                cutout: this.config.isCxOne ? '76%' : undefined, // cutout of 68 makes ring 48 px thick
                animation: {
                    duration: 0,
                },
                hover: {
                    animationDuration: 0,
                },
            },
            plugins: [{
                id: "service-level",
                //white numbers on slices for PM
                afterDatasetsDraw: this.config.isCxOne ? undefined : chartInstance => { // sets the values to the true numbers, rather than the normalized size of each slice.
                    const ctx = chartInstance.ctx;
                    const width = chartInstance.width;
                    const size = Math.round(width / 21);
                    chartInstance.data.datasets.forEach((dataset, i) => {
                        const meta = chartInstance.getDatasetMeta(i);
                        if (!meta.hidden) {
                            meta.data.forEach((element, index) => {
                                if (!chartInstance.getDataVisibility(index))
                                    return;
                                const trueValue = this.getDataByIndex(index);
                                // added if statement so text of zero wouldnt appear on the pie chart with no slice space.
                                if (trueValue === 0)
                                    return;
                                ctx.fillStyle = "#fff";
                                ctx.font = "bold " + size + "px " + this._chartConstants.fontFamily;
                                ctx.textAlign = "center";
                                ctx.textBaseline = "middle";
                                const position = element.tooltipPosition(true);
                                ctx.fillText(trueValue.toString(), position.x, position.y);
                            });
                        }
                    });
                },
                beforeDraw: chart => this.drawCenterLabel(chart),
            }],
        } as ChartConfiguration;
    }


    updateChart() {
        this.chartObject.data.datasets[0].data = [this.data.OutService, this.data.InService];
    }

    /**
     * Returns the value from data pertaining to ChartJS's dataset index.
     * 0 = OutService, 1 = InService
     */
    getDataByIndex(index) {
        switch (index) {
            case 0:
                return this.data && this.data.OutService;
            case 1:
                return this.data && this.data.InService;
            default:
                return undefined;
        }
    }

    private async loadData(isAutoRefresh) {
        if (this.isDataLoading)
            return; // don't have multiple outgoing requests
        this.isDataLoading = true;
        this.somethingWentWrong = false;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        if (this.state == null || this.state.legendPosition == null) {
            this.state = {
                legendPosition: 'top',
                dataPadding: '5% 0 0 0'
            };
        }

        try {
            this.data = await this.service.getServiceLevelData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
            this.createChart();
            this.isDataLoading = false;
            this.setTotalLabelSize();
            this.dataLoadSuccess = true;
            this.moduleManager.setAutoRefreshReady();
            this.isFirstLoad = false;
            this.moduleManager.showLoader = false;
        }
        catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
            this.somethingWentWrong = true;
            return;
        }
        finally {
            this.isDataLoading = false;
        }
    }

    private getLocalization(key: string, defaultVal: string) {
        return this.cvLocalizationService.getCachedText("Service_Level", key, defaultVal);
    }
}
